import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import AddLivestock from "./AddLivestock";
import { addLivestock, deleteLivestock, getLivestock, updateLivestock } from "../lib/api";

const getToken = () => localStorage.getItem("authToken") || localStorage.getItem("token");
const englishDetail = (item) => (item?.LivestockDetails || []).find((detail) =>
  ["en", "en-us", "eng"].includes((detail.GoogleLanguageCode || detail.LanguageCode || "").toLowerCase()),
) || item?.LivestockDetails?.[0] || {};
const preview = (value) => {
  if (!value) return "—";
  return String(value).replace(/<[^>]+>/g, "").slice(0, 90);
};

export default function LivestockList() {
  const [items, setItems] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [livestockToEdit, setLivestockToEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const fetchLivestock = async () => {
    setLoading(true);
    try {
      const response = await getLivestock(getToken());
      setItems(response?.data || []);
    } catch (error) {
      setItems([]);
      toast.error(error?.response?.data?.message || "Failed to load livestock");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLivestock(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, showEntries]);

  const filteredItems = useMemo(() => items.filter((item) => {
    const detail = englishDetail(item);
    const haystack = [
      item.Species,
      detail.CareToBeTaken,
      detail.SelectionPriorities,
      detail.EssentialManagement,
      detail.PriorityHealthModules,
      detail.ProductivityIndicators,
    ].join(" ").toLowerCase();
    return !searchTerm || haystack.includes(searchTerm.toLowerCase());
  }), [items, searchTerm]);

  const totalPages = showEntries === -1 ? 1 : Math.max(1, Math.ceil(filteredItems.length / showEntries));
  const startIndex = showEntries === -1 ? 0 : (currentPage - 1) * showEntries;
  const currentItems = showEntries === -1 ? filteredItems : filteredItems.slice(startIndex, startIndex + showEntries);

  const openDrawer = (item = null) => {
    setLivestockToEdit(item);
    setIsDrawerOpen(true);
  };

  const handleSubmit = async (formData, isUpdate, id) => {
    if (isUpdate) await updateLivestock(getToken(), id, formData);
    else await addLivestock(getToken(), formData);
    await fetchLivestock();
    toast.success(isUpdate ? "Livestock updated successfully" : "Livestock added successfully");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this livestock record?")) return;
    try {
      await deleteLivestock(getToken(), id);
      await fetchLivestock();
      toast.success("Livestock deleted successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete livestock");
    }
  };

  return (
    <>
      <div className="card p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-nowrap gap-2">
            <span className="text-sm fw-medium text-secondary-light">Show</span>
            <select className="form-select form-select-sm" value={showEntries} onChange={(event) => setShowEntries(Number(event.target.value))}>
              <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={-1}>All</option>
            </select>
            <div className="navbar-search"><input className="bg-base h-40-px w-auto" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search livestock" /><Icon icon="ion:search-outline" className="icon" /></div>
          </div>
          <button onClick={() => openDrawer()} className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"><Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />Add livestock</button>
        </div>
        {loading ? <div className="text-center py-4">Loading livestock...</div> : <>
          <div className="table-responsive p-16">
            <table className="table bordered-table sm-table mb-0">
              <thead><tr><th>Species</th><th className="text-center">Status</th><th className="text-center">Action</th></tr></thead>
              <tbody>{currentItems.map((item) => {
                const detail = englishDetail(item);
                return <tr key={item.ID}>
                  <td><span className="text-md fw-normal text-secondary-light">{item.Species}</span></td>
                  <td className="text-center"><span className={`px-16 py-4 radius-4 fw-medium text-sm ${item.IsActive === true || item.IsActive === 1 ? "bg-success-focus text-success-600 border border-success-main" : "bg-neutral-200 text-neutral-600 border border-neutral-400"}`}>{item.IsActive === true || item.IsActive === 1 ? "Active" : "Inactive"}</span></td>
                  <td className="text-center"><div className="d-flex align-items-center gap-10 justify-content-center"><button type="button" className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle" onClick={() => openDrawer(item)}><Icon icon="lucide:edit" className="menu-icon" /></button>{user?.IsDeleteAllowed !== false && <button type="button" className="delete-btn bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle" onClick={() => handleDelete(item.ID)}><Icon icon="lucide:trash-2" className="menu-icon" /></button>}</div></td>
                </tr>;
              })}</tbody>
            </table>
          </div>
          {filteredItems.length === 0 && <div className="text-center py-4 text-muted">No livestock found</div>}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 m-16"><span>Showing {filteredItems.length === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + currentItems.length, filteredItems.length)} of {filteredItems.length} entries</span>{totalPages > 1 && <div className="d-flex gap-2"><button className="btn btn-light btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>Previous</button><span className="align-self-center">{currentPage} / {totalPages}</span><button className="btn btn-light btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)}>Next</button></div>}</div>
        </>}
      </div>
      <AddLivestock isOpen={isDrawerOpen} onClose={() => { setIsDrawerOpen(false); setLivestockToEdit(null); }} onSubmit={handleSubmit} livestockToEdit={livestockToEdit} />
    </>
  );
}